import { cn } from "@babackapp/ui/lib/utils";
import { Column, Heading, Hr, Img, Row, Section, Text } from "@react-email/components";
import React from "react";
import {
  exchangedArticleRefsFromReturnLineItem,
  getExchangedArticle,
  getVariantOptionsLabel,
  shopifyThumbnail,
  type ReturnLineItem,
  type ReturnLineItemResolution,
} from "@babackapp/return-variables-registry";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { useEmailThemeRadius } from "../../hooks";
import { tLayout } from "../../translations";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import type { ReturnLineItemsBlockProps } from "./types";

/** A displayed group is usually a resolution of the returned items, plus
 *  "newOrder" — the articles the customer receives back, which are not a
 *  resolution of anything returned. */
type GroupKind = ReturnLineItemResolution | "newOrder";

type DisplayGroup = { kind: GroupKind; items: ReturnLineItem[] };

const SECTION_LABEL: Partial<Record<GroupKind, { key: string; fallback: string }>> = {
  refund: { key: "returnLineItems.refund", fallback: "Refund" },
  exchange: { key: "returnLineItems.exchange", fallback: "Exchange" },
  newOrder: { key: "returnLineItems.newOrder", fallback: "New order" },
  discount: { key: "returnLineItems.discount", fallback: "Discount" },
};

/**
 * The article sent back when it is *another* article than the returned one
 * (shared `getExchangedArticle` rule): its photo and "Title · 39" caption.
 * Null for a plain size swap, which keeps the "38 → 39" subtitle.
 */
export function getItemExchangedArticle(
  item: ReturnLineItem,
): { image: string; caption: string } | null {
  const exchanged = getExchangedArticle(exchangedArticleRefsFromReturnLineItem(item));
  if (!exchanged) return null;
  return {
    image: exchanged.image,
    caption: exchanged.caption,
  };
}

function getSubtitle(
  item: ReturnLineItem,
  currency: string,
  hidePrices: boolean | undefined,
  hasExchangedArticle: boolean,
): string {
  switch (item.resolution) {
    case "refund": {
      if (hidePrices) return "";
      const price = item.paidPrice ?? item.price;
      return `${price} ${currency}`.trim();
    }
    case "exchange": {
      const original = item.variant?.title ?? "";
      // The chosen article gets its own line, with its photo.
      if (hasExchangedArticle) return original;
      const next = getVariantOptionsLabel([
        item.newVariant?.option1,
        item.newVariant?.option2,
        item.newVariant?.option3,
      ]);
      if (!original && !next) return "";
      return `${original} → ${next}`;
    }
    default:
      return "";
  }
}

function LineItem({
  item,
  currency,
  textColor,
  hidePrices,
}: {
  item: ReturnLineItem;
  currency: string;
  textColor: string;
  hidePrices?: boolean;
}) {
  const name = item.name;
  const imageUrl = item.imageUrl ?? "#";
  const exchanged = getItemExchangedArticle(item);
  const subtitle = getSubtitle(item, currency, hidePrices, !!exchanged);

  return (
    <Row>
      <Column className="w-[64px] p-1">
        <Img
          src={imageUrl}
          width={64}
          height={64}
          alt={name}
          className={cn("w-[64px] h-[64px] rounded-lg object-cover")}
        />
      </Column>
      <Column className="pl-2">
        <Text
          className="m-0 p-0 leading-[1.4] font-semibold"
          style={{ color: textColor }}
        >
          {name} x {item.quantity}
        </Text>
        {subtitle && (
          <Text
            className="m-0 p-0 leading-[1.4] opacity-60"
            style={{ color: textColor }}
          >
            {subtitle}
          </Text>
        )}
        {exchanged && (
          <Row>
            <Column
              className="w-[16px] align-middle"
              style={{ color: textColor }}
            >
              <Text className="m-0 p-0 leading-[1.4]">→</Text>
            </Column>
            <Column className="w-[36px] align-middle">
              <Img
                src={shopifyThumbnail(exchanged.image, 64)}
                width={32}
                height={32}
                alt={exchanged.caption || name}
                className="w-[32px] h-[32px] rounded object-cover"
              />
            </Column>
            <Column className="align-middle pl-1">
              <Text
                className="m-0 p-0 leading-[1.4]"
                style={{ color: textColor }}
              >
                {exchanged.caption}
              </Text>
            </Column>
          </Row>
        )}
        {/* Set by the backend only when the shop shows references. */}
        {item.reference && (
          <Text
            className="m-0 p-0 text-xs leading-[1.4] opacity-60"
            style={{ color: textColor }}
          >
            {item.reference}
          </Text>
        )}
      </Column>
    </Row>
  );
}

function ItemsContainer({
  children,
  contrastColor,
  theme,
}: React.PropsWithChildren & {
  contrastColor: string;
  theme: ReturnLineItemsBlockProps["theme"];
}) {
  const radius = useEmailThemeRadius(theme, "packageContentTable");

  return (
    <Section className={radius}>
      <Row
        className={cn(radius, "border-1 border-solid")}
        style={{ borderWidth: 1, borderColor: contrastColor }}
      >
        <Column>{children}</Column>
      </Row>
    </Section>
  );
}

export function ReturnLineItemsEmailRenderer({
  block,
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnLineItemsBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const { getReturnItems, getReturnExchangeItems, getRefund } =
    useReturnVariableValues();
  const t = tLayout(locale, defaultLocale);

  const items = getReturnItems();
  const exchangeItems = getReturnExchangeItems();
  const { currency } = getRefund();
  const hidePrices = block.properties?.hidePrices;

  if (!items.length && !exchangeItems.length) return null;

  const heading = (kind: GroupKind) => {
    const entry = SECTION_LABEL[kind];
    if (!entry) return kind;
    const label = t(entry.key);
    return label === entry.key ? entry.fallback : label;
  };

  // Build display groups — matching the old React-email templates:
  //  • "exchange" and "exchange-credit" are merged under the "exchange" heading
  //  • "newOrder" holds the new articles shipped back to the customer, the
  //    counterpart of the templates' `exchangeCreditLineItems` section
  //  • Items without a known resolution are order articles not in the return, hidden
  const groups: DisplayGroup[] = [
    {
      kind: "refund",
      items: items.filter((item) => item.resolution === "refund"),
    },
    {
      kind: "exchange",
      // exchange-credit items are grouped with exchange (same section, same heading)
      items: items.filter(
        (item) =>
          item.resolution === "exchange" ||
          item.resolution === "exchange-credit",
      ),
    },
    {
      kind: "newOrder",
      items: exchangeItems,
    },
    {
      kind: "discount",
      items: items.filter((item) => item.resolution === "discount"),
    },
  ];

  const displayGroups = groups.filter((group) => group.items.length > 0);

  return (
    <Section>
      {displayGroups.map((group) => (
        <Section key={group.kind} className="mb-2">
          <Heading as="h5" className="m-0 mb-2 text-xs font-bold text-secondary-foreground">
            {heading(group.kind)}
          </Heading>
          <ItemsContainer theme={theme} contrastColor={colors.contrast}>
            {group.items.map((item, index) => (
              <React.Fragment key={`${group.kind}-${index}`}>
                <LineItem
                  item={item}
                  currency={currency}
                  textColor={colors.text}
                  hidePrices={hidePrices}
                />
                {index < group.items.length - 1 && (
                  <Hr
                    className="border"
                    style={{ borderColor: colors.contrast }}
                  />
                )}
              </React.Fragment>
            ))}
          </ItemsContainer>
        </Section>
      ))}
    </Section>
  );
}
